/**
 * The projected growth of design jobs plays once when it comes on screen: the bars rise from the
 * axis one after another, the arrow draws over them, and the badge pops in at its point. Every cue
 * is the composition's (101 frames at 25 fps), in milliseconds on the elements' data attributes.
 *
 * The arrow is two strokes, the shaft and then the head (modules/motion/stroke.js). The bars rise
 * by translate inside a box that clips them at the axis, and the badge grows by scale, both on the
 * compositor. The pending state holds every part hidden
 * until the piece plays (modules/motion/once.js); the resting markup is the finished chart.
 */
import {EASING_CURVES} from '../../modules/motion/easing.js';
import {cue, playOnce} from '../../modules/motion/once.js';
import {drawStroke} from '../../modules/motion/stroke.js';

const SELECTOR = Object.freeze({
    root: '.js-motionDesignJobsGrowth',
    bar: '.js-motionDesignJobsGrowthBar',
    stroke: '.js-motionDesignJobsGrowthStroke',
    badge: '.js-motionDesignJobsGrowthBadge',
});

const EASE = Object.freeze({
    /* The composition's rise and draw curve, to the nearest named one: a soft start into a long landing. */
    rise: EASING_CURVES['ease-step'],
    pop: EASING_CURVES['ease-out-back'],
});

/** Hold the first keyframe through the delay; the natural styles take over at the end. */
const FILL = 'backwards';

/**
 * @param {HTMLElement} root
 * @param {import('../../modules/motion/run.js').Run} run
 */
function start(root, run) {
    for (const bar of root.querySelectorAll(SELECTOR.bar)) {
        if (bar instanceof HTMLElement) {
            run.add(bar.animate([{translate: '0 100%'}, {translate: '0 0'}], {...cue(bar), easing: EASE.rise, fill: FILL}));
        }
    }
    for (const stroke of root.querySelectorAll(SELECTOR.stroke)) {
        if (stroke instanceof SVGElement) {
            run.add(drawStroke(stroke, {...cue(stroke), easing: EASE.rise}));
        }
    }
    const badge = root.querySelector(SELECTOR.badge);
    if (badge instanceof HTMLElement) {
        run.add(badge.animate([{scale: 0}, {scale: 1}], {...cue(badge), easing: EASE.pop, fill: FILL}));
    }
}

/**
 * Initialize every chart that is not initialized yet. Safe to call after content swaps.
 */
export function initializeComponent() {
    for (const root of document.querySelectorAll(SELECTOR.root)) {
        if (root instanceof HTMLElement) {
            playOnce(root, (run) => start(root, run));
        }
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeComponent);
} else {
    initializeComponent();
}
document.addEventListener('htmx:load', initializeComponent);
